import { useEffect, useRef, useState, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Typography from "@tiptap/extension-typography";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { FontSize } from "@tiptap/extension-text-style";
import { useEditorStore } from "../../stores/editor";
import { useUIStore } from "../../stores/ui";
import { useDocumentStore } from "../../stores/document";
import { DocxGhostText } from "./docx-ghost-text";
import { DocxImage } from "./docx-image";
import { LineSpacing } from "./docx-line-spacing";
import { PageBreaks } from "./page-breaks";
import {
  type PageConfig,
  US_LETTER,
  totalPagesHeight,
  contentAreaInnerMinHeight,
  applyPageCSSVars,
} from "./page-config";
import {
  DocxHeaderFooter,
  DEFAULT_HF,
  type HeaderFooterContent,
  type HFEditingZone,
} from "./docx-header-footer";
import { cn } from "../../lib/cn";
import "./docx-editor.css";

interface DocxEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  className?: string;
  pageConfig?: PageConfig;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function DocxEditor({
  value,
  onChange,
  readOnly = false,
  className,
  pageConfig = US_LETTER,
}: DocxEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(1);
  const [hfContent, setHfContent] = useState<HeaderFooterContent>(DEFAULT_HF);
  const [hfEditing, setHfEditing] = useState<HFEditingZone>(null);

  const {
    setDocxEditor,
    setSelection,
    setCursorLine,
    setShowSelectionToolbar,
    setSelectionToolbarPosition,
  } = useEditorStore();

  const saveDocument = useDocumentStore((s) => s.saveDocument);
  const toolbarAnchor = useUIStore((s) => s.selectionToolbarAnchor);

  const handlePageCountChange = useCallback((n: number) => setPageCount(n), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: "docx-table" },
      }),
      TableRow,
      TableHeader,
      TableCell,
      DocxImage.configure({
        allowBase64: true,
        HTMLAttributes: { class: "docx-image" },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "docx-link" },
      }),
      Placeholder.configure({
        placeholder: "Start typing...",
      }),
      CharacterCount,
      Superscript,
      Subscript,
      Typography,
      TaskList,
      TaskItem.configure({ nested: true }),
      HorizontalRule,
      LineSpacing,
      DocxGhostText,
      PageBreaks.configure({ pageConfig }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(ed.getHTML());
    },
    onSelectionUpdate: ({ editor: ed }) => {
      const { from, to } = ed.state.selection;
      const doc = ed.state.doc;
      let line = 1;
      doc.descendants((_node, nodePos) => {
        if (nodePos >= from) return false;
        if (_node.isBlock) line++;
        return true;
      });
      setCursorLine(line);

      if (from === to) {
        setSelection(null);
        setShowSelectionToolbar(false);
        setSelectionToolbarPosition(null);
        return;
      }

      const text = ed.state.doc.textBetween(from, to, " ");
      setSelection({ from, to, text });

      const view = ed.view;
      const startCoords = view.coordsAtPos(from);
      const endCoords = view.coordsAtPos(to);
      const containerRect = wrapperRef.current?.getBoundingClientRect();
      if (startCoords && endCoords && containerRect) {
        const anchorEnd = toolbarAnchor === "end";
        const midX =
          startCoords.top === endCoords.top
            ? startCoords.left + (endCoords.left - startCoords.left) / 2
            : (containerRect.left + containerRect.right) / 2;
        setSelectionToolbarPosition({
          x: midX - containerRect.left,
          y: anchorEnd
            ? endCoords.bottom - containerRect.top + 8
            : startCoords.top - containerRect.top - 8,
        });
        setShowSelectionToolbar(true);
      }
    },
    editorProps: {
      attributes: {
        class: "docx-editor-content",
        spellcheck: "true",
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "s") {
          event.preventDefault();
          saveDocument();
          return true;
        }
        return false;
      },
      handleDrop: (view, event) => {
        if (!event.dataTransfer?.files?.length) return false;
        const files = Array.from(event.dataTransfer.files).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (files.length === 0) return false;

        event.preventDefault();
        const coords = { left: event.clientX, top: event.clientY };
        const pos = view.posAtCoords(coords);

        files.forEach(async (file) => {
          const dataUrl = await readFileAsBase64(file);
          const img = new Image();
          img.onload = () => {
            const maxWidth = pageConfig.pageWidth - pageConfig.margins.left - pageConfig.margins.right;
            let width = img.naturalWidth;
            let height = img.naturalHeight;
            if (width > maxWidth) {
              const ratio = maxWidth / width;
              width = maxWidth;
              height = Math.round(height * ratio);
            }

            const insertPos = pos?.pos ?? view.state.selection.from;
            const tr = view.state.tr.insert(
              insertPos,
              view.state.schema.nodes.docxImage.create({
                src: dataUrl,
                width,
                height,
                alignment: "center",
              }),
            );
            view.dispatch(tr);
          };
          img.src = dataUrl;
        });

        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (const item of Array.from(items)) {
          if (item.type.startsWith("image/")) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;

            readFileAsBase64(file).then((dataUrl) => {
              const img = new Image();
              img.onload = () => {
                const maxWidth = pageConfig.pageWidth - pageConfig.margins.left - pageConfig.margins.right;
                let width = img.naturalWidth;
                let height = img.naturalHeight;
                if (width > maxWidth) {
                  const ratio = maxWidth / width;
                  width = maxWidth;
                  height = Math.round(height * ratio);
                }

                const { from } = view.state.selection;
                const tr = view.state.tr.insert(
                  from,
                  view.state.schema.nodes.docxImage.create({
                    src: dataUrl,
                    width,
                    height,
                    alignment: "center",
                  }),
                );
                view.dispatch(tr);
              };
              img.src = dataUrl;
            });

            return true;
          }
        }

        return false;
      },
    },
  });

  useEffect(() => {
    if (wrapperRef.current) {
      applyPageCSSVars(wrapperRef.current, pageConfig);
    }
  }, [pageConfig]);

  useEffect(() => {
    if (editor) {
      setDocxEditor(editor);
      (editor.storage as unknown as Record<string, Record<string, unknown>>).pageBreaks.onPageCountChange = handlePageCountChange;
    }
    return () => {
      setDocxEditor(null);
    };
  }, [editor, setDocxEditor, handlePageCountChange]);

  useEffect(() => {
    if (!editor) return;
    const currentHTML = editor.getHTML();
    if (currentHTML !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer?.types?.includes("Files")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const pagesColumnHeight = totalPagesHeight(pageConfig, pageCount);
  const innerMinHeight = contentAreaInnerMinHeight(pageConfig, pageCount);

  return (
    <div
      ref={wrapperRef}
      className={cn("docx-editor-wrapper h-full w-full overflow-auto", className)}
      onDragOver={handleDragOver}
    >
      <div className="docx-pages-column" style={{ height: pagesColumnHeight }}>
        {Array.from({ length: pageCount }, (_, i) => (
          <div
            key={i}
            className="docx-page-bg"
            style={{
              top: i * (pageConfig.pageHeight + pageConfig.pageGap),
              height: pageConfig.pageHeight,
            }}
          />
        ))}

        <DocxHeaderFooter
          pageCount={pageCount}
          pageHeight={pageConfig.pageHeight}
          pageGap={pageConfig.pageGap}
          marginTop={pageConfig.margins.top}
          marginBottom={pageConfig.margins.bottom}
          marginLeft={pageConfig.margins.left}
          marginRight={pageConfig.margins.right}
          content={hfContent}
          onContentChange={setHfContent}
          editingZone={hfEditing}
          onEditingZoneChange={setHfEditing}
        />

        <div
          className={cn("docx-content-area", hfEditing && "docx-content-area-dimmed")}
          style={{ minHeight: innerMinHeight }}
          onClick={() => {
            if (hfEditing) setHfEditing(null);
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      <div className="docx-page-indicator">
        {pageCount} {pageCount === 1 ? "page" : "pages"}
      </div>
    </div>
  );
}
